import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Stack } from '@mui/material'
import Step from '@mui/material/Step'
import StepButton from '@mui/material/StepButton'
import Stepper from '@mui/material/Stepper'
import { ChevronLeftRounded } from '@mui/icons-material'
import { Instance } from 'mobx-state-tree'

import { Dialog } from '@trivie/ui-web/components/dialogs/dialog'
import { ContestCreateForm } from '@trivie/core/src/models/ContestCreateForm'
import { ContestApi } from '@trivie/core/src/services/api-objects/ContestApi'
import { ContestHelper, useStores } from '@trivie/core'
import { Text } from '@trivie/ui-web/components/text'
import { Button } from '@trivie/ui-web/components/button'
import { color } from '@trivie/ui-web/theme/color'

import { ContestCompetitors, ContestDetails, ContestScoring, ContestContent } from './steps'

type CreateContestWizardProps = {
  open: boolean | undefined
  editingId?: number | null
  requestClose: () => void
}

export const CreateContestWizard = observer((props: CreateContestWizardProps) => {
  const { contestStore } = useStores()
  const { getContest, setTriggerRefresh } = contestStore
  const { open, editingId, requestClose } = props
  const [activeStep, setActiveStep] = useState(0)
  const [form, setForm] = useState<Instance<typeof ContestCreateForm> | null>(null)
  const [preselectIds, setPreselectIds] = useState<{ id: number; type: string }[] | undefined>()
  const steps = ['Competitors', 'Scoring', 'Content', 'Details']

  useEffect(() => {
    if (open) {
      setActiveStep(0)
      setPreselectIds(undefined)

      if (editingId) {
        getContest(editingId).then((data) => {
          const formDefaults = ContestHelper.createContestForm(data)
          formDefaults.set('custom_scoring', {
            ...formDefaults.custom_scoring,
          })
          setForm(formDefaults)

          const ids: { id: number; type: string }[] = []
          if (data.topic_ids?.length) {
            data.topic_ids.forEach((id: number) => ids.push({ id, type: 'topics' }))
          }
          if (data.quiz_ids?.length) {
            data.quiz_ids.forEach((id: number) => ids.push({ id, type: 'quizzes' }))
          }
          setPreselectIds(ids.length > 0 ? ids : undefined)
        })
      } else {
        const formDefaults = ContestHelper.createContestForm({})
        formDefaults.set('custom_scoring', {
          ...formDefaults.custom_scoring,
          answers: true,
          badges: false,
          comments: false,
          posts: false,
          reinforcements: false,
          surveys: false,
          tests: false,
        })
        setForm(formDefaults)
      }
    }
  }, [open])

  if (!form) {
    return null
  }

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <ContestCompetitors editingId={editingId} form={form} />
      case 1:
        return <ContestScoring form={form} />
      case 2:
        return <ContestContent form={form} preselectIds={preselectIds} />
      case 3:
        return <ContestDetails form={form} />
      case 4:
        return (
          <div style={CONFIRM}>
            <Text
              style={{ color: color.shade70 }}
              variant="body1"
              text={
                editingId
                  ? 'Are you sure you want to edit this contest?'
                  : 'This will create a new contest. Are you sure?'
              }
            />
          </div>
        )
      case 5:
        return (
          <div style={CONFIRM}>
            <Text
              style={{ color: color.shade70 }}
              variant="body1"
              text={editingId ? 'Contest edited!' : 'Contest created!'}
            />
          </div>
        )
      default:
        return 'Unknown step'
    }
  }

  const isLastStep = () => activeStep > steps.length

  const handleNext = async () => {
    if (activeStep === 4) {
      const { payload } = form
      if (editingId) {
        ContestApi.editContest(payload).then(() => {
          setTriggerRefresh(true)
        })
      } else {
        ContestApi.createContest(payload).then(() => {
          setTriggerRefresh(true)
        })
      }
      setActiveStep(5)
    } else if (activeStep === 5) {
      setActiveStep(0)
      requestClose()
    } else {
      const newActiveStep = !isLastStep() ? activeStep + 1 : activeStep
      setActiveStep(newActiveStep)
    }
  }

  const handleBack = () => setActiveStep((prevActiveStep) => prevActiveStep - 1)

  const stepIsValid = () => {
    if (activeStep === 0) {
      return Boolean(form.user_ids.length > 0 || form.group_ids.length > 0)
    } else if (activeStep === 1) {
      return !form.emptyScoring
    } else if (activeStep === 2) {
      if (
        form.content_method === 'standard' ||
        (form.content_method === 'custom' &&
          (form.selectedQuizzes.length || form.selectedTopics.length > 0))
      ) {
        return true
      }
      return false
    } else if (activeStep === 3) {
      return !Object.keys(form.errors).length
    } else {
      return true
    }
  }

  const getButtonText = () => {
    if (editingId && activeStep === 3) {
      return 'Save'
    } else if (activeStep === 3) {
      return 'Create'
    } else if (activeStep === 4) {
      return 'Confirm'
    } else if (activeStep === 5) {
      return 'Close'
    } else {
      return 'Next'
    }
  }

  const handleClose = () => {
    setActiveStep(0)
    requestClose()
  }

  return (
    <Dialog
      onClose={handleClose}
      title={editingId ? 'Edit Contest' : 'Create Contest'}
      open={Boolean(open)}
      maxWidth={activeStep < 4 ? 'md' : 'xs'}
      disableBackdropClick
      sx={{
        '& .MuiDialogContent-root': {
          display: 'flex',
          flexDirection: 'column',
          height: activeStep < 4 ? 600 : 'auto',
        },
      }}
      actions={
        <Stack
          flex={1}
          direction="row"
          useFlexGap
          gap="12px"
          justifyContent={activeStep === 0 || activeStep === 5 ? 'flex-end' : 'space-between'}
        >
          {activeStep > 0 && activeStep < 5 && (
            <Button
              onClick={handleBack}
              startIcon={<ChevronLeftRounded />}
              variant="text"
              text="Back"
            />
          )}
          <Button onClick={handleNext} disabled={!stepIsValid()} text={getButtonText()} />
        </Stack>
      }
    >
      {activeStep < 4 && (
        <Stepper
          style={{ paddingLeft: 0, paddingRight: 0, marginBottom: 32 }}
          nonLinear
          activeStep={activeStep}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepButton disableRipple disabled>
                {label}
              </StepButton>
            </Step>
          ))}
        </Stepper>
      )}
      <div
        style={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          overflowY: 'hidden',
          height: activeStep < 4 ? 500 : 'auto',
        }}
      >
        {getStepContent(activeStep)}
      </div>
    </Dialog>
  )
})

const CONFIRM: React.CSSProperties = {
  display: 'flex',
  flex: 1,
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
}
